"use client";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useRouter } from "next/navigation";
import { useState } from "react";
import Blockies from "react-blockies";
import Marquee from "react-fast-marquee";
import { useAccount, useBalance, useDisconnect } from "wagmi";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

import { useENS } from "../hooks/useENS";
import { Button } from "./Button";
import StaticTrack from "./StaticTrack";

export function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export const FEATURED_IDS = ["60", "61", "68", "75", "91"];

const Menu = ({ today, current }: { today: bigint; current: bigint }) => {
  const router = useRouter();
  const { openConnectModal } = useConnectModal();
  const { address, isConnected } = useAccount();
  const { data: ensData } = useENS({ address });
  const { disconnect } = useDisconnect();
  const { data: balanceData } = useBalance({ address, chainId: 8453 });

  const previous = [
    BigInt(today) - 2n,
    BigInt(today) - 1n,
    BigInt(today),
  ].filter((id) => id !== current);

  return (
    <div className="flex flex-col flex-shrink  py-6 px-4  bg-white md:px-6 rounded-md z-[12] border-2 border-black">
      <Carousel className="w-full flex flex-col max-w-sm m-auto mb-4">
        <CarouselContent className="mb-6">
          {previous.map((id, index) => (
            <CarouselItem
              key={id.toString(10)}
              className="overflow-hidden relative basis-1/3 md:basis-1/2 lg:basis-1/3 cursor-pointer"
            >
              <div>
                <Card
                  className="aspect-[8/13] rounded-none border-0"
                  onClick={() => {
                    router.push(`/${id.toString(10)}`);
                  }}
                >
                  <CardContent className="relative w-full h-full rounded-lg">
                    <StaticTrack seed={id.toString(10)} showAds ignoreTheme />
                  </CardContent>
                </Card>
              </div>
            </CarouselItem>
          ))}
          {FEATURED_IDS.filter((id) => BigInt(id) !== current).map(
            (id, index) => (
              <CarouselItem
                key={id}
                className="overflow-hidden relative basis-1/3 md:basis-1/2 lg:basis-1/3 cursor-pointer"
              >
                <div>
                  <Card
                    className="aspect-[8/13] rounded-none border-0"
                    onClick={() => {
                      router.push(`/${id}`);
                    }}
                  >
                    <CardContent className="relative w-full h-full rounded-lg">
                      <StaticTrack seed={id} showAds ignoreTheme />
                    </CardContent>
                  </Card>
                </div>
              </CarouselItem>
            )
          )}
          <CarouselItem className="overflow-hidden select-none relative basis-1/3 md:basis-1/2 lg:basis-1/3 cursor-pointer">
            <div>
              <Card
                className="aspect-[8/13] rounded-none border-0"
                onClick={() => {
                  router.push(`/${getRandomInt(1, 130)}`);
                }}
              >
                <CardContent className="bg-green p-2 text-center flex flex-col justify-center items-center relative w-full h-full">
                  <span className="text-6xl mb-2">🔄</span>
                  Random Track
                </CardContent>
              </Card>
            </div>
          </CarouselItem>
        </CarouselContent>
        <div>
          <div className="w-1/5 m-auto space-x-2 relative flex flex-row justify-center items-center">
            <CarouselPrevious
              className="relative"
              style={{ translate: "none", left: "unset" }}
            />
            <CarouselNext
              className="relative"
              style={{ translate: "none", right: "unset" }}
            />
          </div>
        </div>
      </Carousel>

      <div>
        {isConnected ? (
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center justify-start space-x-2">
              <Avatar>
                <AvatarImage
                  width={48}
                  height={48}
                  alt={address}
                  src={ensData?.avatar}
                />
                <AvatarFallback>
                  <Blockies seed={address} />
                </AvatarFallback>
              </Avatar>
              <div className="ml-4 overflow-hidden text-xl flex flex-col justify-center">
                <span data-tooltip={address}>
                  {ensData?.displayName?.length > 25 ? (
                    <Marquee>
                      <span className="mr-8">{ensData?.displayName}</span>
                    </Marquee>
                  ) : (
                    ensData?.displayName
                  )}
                </span>
                <span
                  onClick={() => disconnect()}
                  className="text-sm text-red cursor-pointer"
                >
                  Disconnect
                </span>
              </div>
            </div>
            <div className="flex flex-col justify-center items-center">
              <p
                className={
                  balanceData?.value < 1600000000000000n ? "text-red" : ""
                }
              >
                {parseFloat(balanceData?.formatted || "0").toFixed(4)} ETH on
                Base
              </p>
              <a
                target="_blank"
                href="https://bridge.base.org/deposit"
                className="text-xs text-blue"
                rel="noreferrer"
              >
                Bridge to Base
              </a>
            </div>
          </div>
        ) : (
          <Button
            className="text-center justify-center w-full"
            onClick={openConnectModal}
          >
            Connect Wallet
          </Button>
        )}
      </div>
    </div>
  );
};

export default Menu;
